import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  FileText, CheckCircle, Clock, XCircle, 
  Loader2, RefreshCcw, IndianRupee, Calendar,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../utils/apiUrl';

const formatCurrency = (value) => {
  if (!value) return '₹0';
  return `₹${value.toLocaleString('en-IN')}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  } catch {
    return '-';
  }
};

const IncentiveReportsPage = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('activations');
  const [loading, setLoading] = useState(true);
  const [activations, setActivations] = useState([]);
  const [pending, setPending] = useState([]);
  const [expired, setExpired] = useState([]);

  useEffect(() => {
    fetchAllReports();
  }, []);

  const fetchAllReports = async () => {
    setLoading(true);
    try {
      const [activationsRes, pendingRes, expiredRes] = await Promise.all([
        fetch(`${API_URL}/reports/dormant-activations`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/reports/pending-activations`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/reports/expired-activations`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (activationsRes.ok) setActivations(await activationsRes.json());
      if (pendingRes.ok) setPending(await pendingRes.json());
      if (expiredRes.ok) setExpired(await expiredRes.json());
    } catch (err) {
      console.error('Failed to fetch reports:', err);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#C5A059]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="incentive-reports-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <FileText className="w-6 h-6 text-[#C5A059]" />
            Incentive Reports
          </h1>
          <p className="text-slate-400 mt-1">Dormant activation tracking and reporting</p>
        </div>
        <Button 
          variant="outline" 
          onClick={fetchAllReports}
          className="border-[#C5A059]/30"
        >
          <RefreshCcw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-emerald-500/10 border-emerald-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-300">Completed Activations</p>
                <p className="text-3xl font-bold text-white mt-1">{activations.length}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-emerald-400 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-yellow-500/10 border-yellow-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-yellow-300">Pending Activations</p>
                <p className="text-3xl font-bold text-white mt-1">{pending.length}</p>
              </div>
              <Clock className="w-10 h-10 text-yellow-400 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-red-500/10 border-red-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-300">Expired (Missed)</p>
                <p className="text-3xl font-bold text-white mt-1">{expired.length}</p>
              </div>
              <XCircle className="w-10 h-10 text-red-400 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-800/50">
          <TabsTrigger value="activations" className="data-[state=active]:bg-emerald-600">
            <CheckCircle className="w-4 h-4 mr-2" />
            Completed ({activations.length})
          </TabsTrigger>
          <TabsTrigger value="pending" className="data-[state=active]:bg-yellow-600">
            <Clock className="w-4 h-4 mr-2" />
            Pending ({pending.length})
          </TabsTrigger>
          <TabsTrigger value="expired" className="data-[state=active]:bg-red-600">
            <XCircle className="w-4 h-4 mr-2" />
            Expired ({expired.length})
          </TabsTrigger>
        </TabsList>

        {/* Completed Activations */}
        <TabsContent value="activations">
          <Card className="card-dark border-white/5">
            <CardHeader>
              <CardTitle className="text-white">Dormant Activation Report</CardTitle>
              <CardDescription>Clients successfully re-activated with brokerage within window</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10">
                    <TableHead className="text-slate-400">Client</TableHead>
                    <TableHead className="text-slate-400">Dormant Category</TableHead>
                    <TableHead className="text-slate-400">Meeting Date</TableHead>
                    <TableHead className="text-slate-400">Brokerage Date</TableHead>
                    <TableHead className="text-slate-400">Staff</TableHead>
                    <TableHead className="text-slate-400 text-right">Brokerage</TableHead>
                    <TableHead className="text-slate-400 text-right">Incentive</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activations.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-slate-400">
                        No completed activations yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    activations.map((a, idx) => (
                      <TableRow key={idx} className="border-white/5 hover:bg-white/5">
                        <TableCell>
                          <div>
                            <span className="text-white font-medium">{a.client_name || a.client_code}</span>
                            <span className="text-slate-500 text-xs block">{a.client_code}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            a.dormant_category === 'dormant_3' ? 'bg-red-600' : 'bg-amber-600'
                          }>
                            {a.dormant_category === 'dormant_3' ? 'Dormant 3' : 'Dormant 2'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-slate-300">{formatDate(a.meeting_date)}</TableCell>
                        <TableCell className="text-slate-300">{formatDate(a.brokerage_date)}</TableCell>
                        <TableCell className="text-slate-300">{a.user_name}</TableCell>
                        <TableCell className="text-right text-emerald-400">
                          {formatCurrency(a.brokerage_amount)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge className="bg-[#C5A059]">
                            {formatCurrency(a.incentive_amount)}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pending Activations */}
        <TabsContent value="pending">
          <Card className="card-dark border-white/5">
            <CardHeader>
              <CardTitle className="text-white">Pending Dormant Activations</CardTitle>
              <CardDescription>Meeting completed, awaiting brokerage within window</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10">
                    <TableHead className="text-slate-400">Client</TableHead>
                    <TableHead className="text-slate-400">Dormant Category</TableHead>
                    <TableHead className="text-slate-400">Meeting Date</TableHead>
                    <TableHead className="text-slate-400">Deadline</TableHead>
                    <TableHead className="text-slate-400">Staff</TableHead>
                    <TableHead className="text-slate-400 text-center">Days Left</TableHead>
                    <TableHead className="text-slate-400 text-center">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pending.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-slate-400">
                        No pending activations
                      </TableCell>
                    </TableRow>
                  ) : (
                    pending.map((p, idx) => (
                      <TableRow key={idx} className="border-white/5 hover:bg-white/5">
                        <TableCell>
                          <div>
                            <span className="text-white font-medium">{p.client_name || p.client_code}</span>
                            <span className="text-slate-500 text-xs block">{p.client_code}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            p.dormant_category === 'dormant_3' ? 'bg-red-600' : 'bg-amber-600'
                          }>
                            {p.dormant_category === 'dormant_3' ? 'Dormant 3' : 'Dormant 2'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-slate-300">{formatDate(p.meeting_date)}</TableCell>
                        <TableCell className="text-slate-300">{formatDate(p.deadline_date)}</TableCell>
                        <TableCell className="text-slate-300">{p.user_name}</TableCell>
                        <TableCell className="text-center">
                          <span className={`font-bold ${
                            p.days_remaining <= 7 ? 'text-red-400' : 
                            p.days_remaining <= 14 ? 'text-yellow-400' : 'text-emerald-400'
                          }`}>
                            {p.days_remaining}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          {p.days_remaining <= 7 ? (
                            <Badge className="bg-red-600">
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              Urgent
                            </Badge>
                          ) : (
                            <Badge className="bg-yellow-600">
                              <Clock className="w-3 h-3 mr-1" />
                              Pending
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Expired Activations */}
        <TabsContent value="expired">
          <Card className="card-dark border-white/5">
            <CardHeader>
              <CardTitle className="text-white">Expired Activations</CardTitle>
              <CardDescription>Meeting completed but brokerage not generated within window</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10">
                    <TableHead className="text-slate-400">Client</TableHead>
                    <TableHead className="text-slate-400">Dormant Category</TableHead>
                    <TableHead className="text-slate-400">Meeting Date</TableHead>
                    <TableHead className="text-slate-400">Deadline</TableHead>
                    <TableHead className="text-slate-400">Staff</TableHead>
                    <TableHead className="text-slate-400 text-center">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {expired.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-slate-400">
                        No expired activations
                      </TableCell>
                    </TableRow>
                  ) : (
                    expired.map((e, idx) => (
                      <TableRow key={idx} className="border-white/5 hover:bg-white/5">
                        <TableCell>
                          <div>
                            <span className="text-white font-medium">{e.client_name || e.client_code}</span>
                            <span className="text-slate-500 text-xs block">{e.client_code}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            e.dormant_category === 'dormant_3' ? 'bg-red-600' : 'bg-amber-600'
                          }>
                            {e.dormant_category === 'dormant_3' ? 'Dormant 3' : 'Dormant 2'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-slate-300">{formatDate(e.meeting_date)}</TableCell>
                        <TableCell className="text-slate-300">{formatDate(e.deadline_date)}</TableCell>
                        <TableCell className="text-slate-300">{e.user_name}</TableCell>
                        <TableCell className="text-center">
                          <Badge className="bg-red-600/50 text-red-300">
                            <XCircle className="w-3 h-3 mr-1" />
                            Missed
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default IncentiveReportsPage;
